import { EventrDate, IEventrActivity } from '@eventr/common'
import { ColoursObj } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { CountLabel } from '../../common/CountLabel'
import { useScreenTypeContext } from '../state/screenTypeProvider'
import { usePendingTodoCount } from './hooks/usePendingTodoCount'
import { ObjectProvider } from './providers/objectProvider'
import { BaseCard } from './subComponents/baseCard'
import { DraftMessage } from './subComponents/draftMessage'
import { TodoBookingInfo } from './todoComponents/todoBookingInfo'
import { TodoHeader } from './todoComponents/todoHeader'
import { CardText } from './subComponents/cardText'
interface TodoCardProps {
    id?: string
    todo: IEventrActivity
}
export type TodoRange = 'normal' | 'done' | 'danger' | 'warning'

export const TodoCard = ({ id, todo }: TodoCardProps) => {
    const { screenType } = useScreenTypeContext()

    const pendingCount = usePendingTodoCount(todo)

    const onClick = useCallback(() => {
        if (screenType === 'edit') {
            return
        }
        let query = ''
        if (screenType === 'team') {
            query = '?noEdit=true'
        }
        window.pushPath(`/item/todo/${todo._id}${query}`)
    }, [todo._id, screenType])
    const count = useMemo(() => {
        if (screenType === 'edit') return todo?.tasks?.length ?? 0
        return pendingCount
    }, [pendingCount, screenType, todo.tasks])

    const range: TodoRange = useMemo(() => {
        if (screenType === 'edit') return 'normal'
        if (count === 0) return 'done'
        const now = new Date().getTime()
        if (!todo.due_date && todo.start_by) {
            const start_by = new EventrDate(todo.start_by)
            if (start_by.getUnixMs - now < 0) {
                return 'warning'
            }
        }
        if (!todo.due_date) return 'normal'
        const date = new EventrDate(todo.due_date)
        const start_by = new EventrDate(todo.start_by ?? todo.due_date)
        const diff = date.getUnixMs - now
        if (diff < 0) return 'danger'
        if (start_by.getUnixMs - now < 0) return 'warning'
        return 'normal'
    }, [todo.due_date, screenType, count, todo.start_by])

    const borderColor = useMemo(() => {
        switch (range) {
            case 'danger':
                return ColoursObj.states.error
            case 'warning':
                return ColoursObj.states.warning
            case 'done':
                return ColoursObj.shades.shade2
            default:
                return ColoursObj.core.secondary
        }
    }, [range])

    if (screenType === 'upNext' && count === 0) return null

    return (
        <ObjectProvider
            past={range === 'done'}
            _id={todo._id}
            sectionID="8"
            visibility={todo.visibility}
        >
            <BaseCard
                id={id}
                onClick={onClick}
                borderStyle={
                    range === 'normal' ? 'none' : `2px solid ${borderColor}`
                }
            >
                <TodoHeader
                    colour={borderColor}
                    todoName={todo.todo_name}
                    dueDate={todo.due_date}
                    range={range}
                />
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    width="100%"
                >
                    <CardText
                        useMarkdown
                    >
                        {todo.todo_description}
                    </CardText>
                    <CountLabel text={count} bgColor={borderColor} />
                </Stack>
                {screenType === 'edit' && (
                    <TodoBookingInfo
                        attachments={{
                            extra_docs: todo?.attendees_list?.[0]?.documents,
                            notes: todo?.attendees_list?.[0]?.notes,
                        }}
                        attendess={todo.attendees_list}
                    />
                )}
                <DraftMessage data={todo} />
            </BaseCard>
        </ObjectProvider>
    )
}
